import { useMutation } from '@apollo/client';
import { createContext, useContext, useReducer } from 'react';
import { useTranslation } from 'react-i18next';
import { useTutorialOverlay } from '../hooks/useTutorialOverlay';
import { SET_TUTORIAL_SEEN } from '../http/apollo-queries';
import { useAuth } from './Auth';
import { useUserStrategies } from './UserStrategies';

type State = {
  isTooltipIconVisible: boolean;
  dispatch(value: Partial<State>): void;
  handleTooltipIconVisibility(tutorial: Tutorial): void;
  // ALIGN
  AlignTutorial(): JSX.Element;
  AlignButtonTooltip: TooltipComponent;
  AlignFirstAssetTooltip: TooltipComponent;

  // COMPARE
  CompareTutorial(): JSX.Element;
  ComparePerformanceTooltip: TooltipComponent;

  // STRATEGIES
  StrategiesTutorial(): JSX.Element;
  StrategiesAlignButtonTooltip: TooltipComponent;
  StrategiesFirstStrategyTooltip: TooltipComponent;
  StrategiesNavButtonTooltip: TooltipComponent;
};

export type TooltipComponent = ({
  className,
  tooltipClassname,
  tooltipPopupClassname,
}: {
  className?: string;
  tooltipClassname?: string;
  tooltipPopupClassname?: string;
}) => JSX.Element;

const reducer = (state: State, updates: Partial<State>): State => {
  return { ...state, ...updates };
};

const localState: State = {
  isTooltipIconVisible: false,
  dispatch: () => {},
  handleTooltipIconVisibility: () => {},

  // ALIGN
  AlignTutorial: () => <></>,
  AlignButtonTooltip: () => <></>,
  AlignFirstAssetTooltip: () => <></>,

  // COMPARE
  CompareTutorial: () => <></>,
  ComparePerformanceTooltip: () => <></>,

  // STRATEGIES
  StrategiesTutorial: () => <></>,
  StrategiesAlignButtonTooltip: () => <></>,
  StrategiesFirstStrategyTooltip: () => <></>,
  StrategiesNavButtonTooltip: () => <></>,
};

const TutoarialContext = createContext(localState);

type Tutorial = 'ALIGN' | 'COMPARE' | 'MERGE' | 'STRATEGIES' | 'WORKSPACE';

export const TutorialProvider = ({
  children,
}: {
  children: JSX.Element | JSX.Element[];
}) => {
  const { t } = useTranslation();

  const { user, refetch: userRefetch } = useAuth();
  const { strategies } = useUserStrategies();

  const [state, dispatch] = useReducer(reducer, localState);
  const { isTooltipIconVisible } = state;

  const { showTutorial } = user?.profile || {
    showTutorial: {
      ALIGN: true,
      COMPARE: true,
      MERGE: true,
      STRATEGIES: true,
      WORKSPACE: true,
    },
  };

  const { ALIGN, COMPARE, STRATEGIES } = showTutorial;

  const [setTutorialSeen] = useMutation(SET_TUTORIAL_SEEN);

  const handleTooltipIconVisibility = (tutorial: Tutorial) => {
    showTutorial[tutorial] &&
      dispatch({ isTooltipIconVisible: showTutorial[tutorial] });
  };

  const onCloseTutorial = (tutorial: Tutorial) => {
    dispatch({ isTooltipIconVisible: false });
    setTutorialSeen({
      variables: {
        profileId: user?.profile?.id!,
        tutorial,
      },
    }).then(() => userRefetch());
  };

  const {
    TutorialOverlay: StrategiesTutorial,
    tooltips: [
      StrategiesNavButtonTooltip,
      StrategiesFirstStrategyTooltip,
      StrategiesAlignButtonTooltip,
    ],
  } = useTutorialOverlay({
    isTooltipIconVisible:
      user?.profile?.creationStatus === 'COMPLETED' &&
      strategies.length > 0 &&
      (STRATEGIES || isTooltipIconVisible),
    isTutorialShown: STRATEGIES && strategies.length > 0,
    tooltipsArray: [...Array(3)].map((_, i) => t(`tooltips.strategies.${i}`)),
    tooltipClassname: 'mr-1.5',
    onCloseTutorial: () => onCloseTutorial('STRATEGIES'),
  });

  const {
    TutorialOverlay: CompareTutorial,
    tooltips: [ComparePerformanceTooltip],
  } = useTutorialOverlay({
    isTooltipIconVisible:
      strategies.length > 1 && (COMPARE || isTooltipIconVisible),
    isTutorialShown: COMPARE && strategies.length > 1,
    tooltipsArray: [...Array(1)].map((_, i) => t(`tooltips.compare.${i}`)),
    tooltipClassname: 'mr-1.5',
    onCloseTutorial: () => onCloseTutorial('COMPARE'),
  });

  const {
    TutorialOverlay: AlignTutorial,
    tooltips: [AlignButtonTooltip, AlignFirstAssetTooltip],
  } = useTutorialOverlay({
    isTooltipIconVisible:
      user?.membership?.membershipPlan?.sku !== 'Camelot' &&
      (ALIGN || isTooltipIconVisible),
    isTutorialShown: !user?.membership?.sku?.includes('Camelot') && ALIGN,
    tooltipsArray: [...Array(1)].map((_, i) => t(`tooltips.align.${i}`)),
    tooltipClassname: 'mr-1.5',
    onCloseTutorial: () => onCloseTutorial('ALIGN'),
  });

  const value = {
    ...state,
    dispatch,
    handleTooltipIconVisibility,

    // ALIGN
    AlignTutorial,
    AlignButtonTooltip,
    AlignFirstAssetTooltip,

    // COMPARE
    CompareTutorial,
    ComparePerformanceTooltip,

    // STRATEGIES
    StrategiesTutorial,
    StrategiesAlignButtonTooltip,
    StrategiesFirstStrategyTooltip,
    StrategiesNavButtonTooltip,
  };

  return (
    <TutoarialContext.Provider value={value}>
      {children}
    </TutoarialContext.Provider>
  );
};

export const useTutorialContext = () => useContext(TutoarialContext);
